(function (root) {
  const VcTicker = {
    copiesNeeded(listWidth, barWidth) {
      if (!(listWidth > 0)) return 0;
      return Math.max(2, Math.ceil(barWidth / listWidth) + 1);
    },
    durationSeconds(distance, pxPerSecond) {
      if (!(distance > 0) || !(pxPerSecond > 0)) return 0;
      return Math.round((distance / pxPerSecond) * 100) / 100;
    },
    shouldAnimate(reducedMotion, copies) {
      return !reducedMotion && copies >= 2;
    },
    visibleFor(countryLists, cc) {
      const named = new Set();
      countryLists.forEach((codes) => {
        if (codes !== 'ALL' && codes !== 'OTHERS') codes.split(',').forEach((code) => named.add(code));
      });
      return countryLists.map((codes) =>
        codes === 'ALL' || (codes === 'OTHERS' && !named.has(cc)) || codes.split(',').includes(cc));
    },
    parseUtc(value) {
      if (value == null || value === '') return null;
      return /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\dZ$/.test(value) ? Date.parse(value) : NaN;
    },
    scheduleState(nowMs, startMs, endMs, lastDayMs = 86400000) {
      if (Number.isNaN(startMs) || Number.isNaN(endMs)) return 'invalid';
      if (startMs != null && nowMs < startMs) return 'before';
      if (endMs != null && nowMs >= endMs) return 'after';
      if (endMs != null && nowMs >= endMs - lastDayMs) return 'lastday';
      return 'live';
    },
  };
  root.VcTicker = VcTicker;

  if (typeof customElements === 'undefined' || customElements.get('vc-ticker-bar')) return;

  const PAUSE_KEY = 'vc-ticker-paused';
  const readPaused = () => {
    try { return localStorage.getItem(PAUSE_KEY) === '1'; } catch (e) { return false; }
  };
  const writePaused = (value) => {
    try { localStorage.setItem(PAUSE_KEY, value ? '1' : '0'); } catch (e) { /* storage blocked */ }
  };

  class VcTickerBar extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('[data-ticker-viewport]');
      this.track = this.querySelector('[data-ticker-track]');
      this.list = this.querySelector('[data-ticker-list]');
      this.pauseButton = this.querySelector('[data-ticker-pause]');
      this.speed = Number(this.dataset.speed) || 40;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = readPaused();
      this.lastWidth = 0;
      this.lastListWidth = 0;
      this.build = this.build.bind(this);

      this.resizeObserver = new ResizeObserver(() => {
        if (this.viewport.clientWidth !== this.lastWidth || this.list.scrollWidth !== this.lastListWidth) this.build();
      });
      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.list);
      this.motion.addEventListener('change', this.build);
      document.fonts?.ready.then(this.build);

      this.pauseButton?.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        writePaused(this.userPaused);
        this.setPaused(this.userPaused);
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.viewport.scrollLeft = 0;
      });

      this.applySchedule(this.list);
      const allTemplate = this.querySelector('[data-ticker-all]');
      if (allTemplate) this.applySchedule(allTemplate.content);
      this.build();
      if (allTemplate) this.checkIp();
    }

    applySchedule(scope) {
      const now = Date.now();
      scope.querySelectorAll('.vc-ticker__item[data-starts], .vc-ticker__item[data-ends]').forEach((item) => {
        const state = VcTicker.scheduleState(now, VcTicker.parseUtc(item.dataset.starts), VcTicker.parseUtc(item.dataset.ends));
        if (state === 'before' || state === 'after' || state === 'invalid') { item.remove(); return; }
        item.hidden = false;
        const msg = item.querySelector('.vc-ticker__msg');
        if (state === 'lastday' && item.dataset.lastDay && msg) msg.textContent = item.dataset.lastDay;
      });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.motion?.removeEventListener('change', this.build);
    }

    async checkIp() {
      const template = this.querySelector('[data-ticker-all]');
      let cc = '';
      try {
        const response = await fetch('/browsing_context_suggestions.json');
        const data = await response.json();
        cc = (data.detected_values?.country?.handle || '').toUpperCase();
      } catch (e) {
        return;
      }
      if (!cc || cc === this.dataset.defaultCountry) return;
      const items = [...template.content.querySelectorAll('.vc-ticker__item')];
      const keep = VcTicker.visibleFor(items.map((item) => item.dataset.countries), cc);
      this.list.replaceChildren(...items.filter((item, i) => keep[i]).map((item) => item.cloneNode(true)));
      this.build();
    }

    build() {
      this.hidden = !this.list.querySelector('.vc-ticker__item:not([hidden])');
      if (this.hidden) return;
      this.lastWidth = this.viewport.clientWidth;
      this.track.querySelectorAll('[data-ticker-clone]').forEach((node) => node.remove());
      this.removeAttribute('data-static');
      this.removeAttribute('data-animated');

      const listWidth = this.list.getBoundingClientRect().width;
      const copies = VcTicker.copiesNeeded(listWidth, this.lastWidth);
      if (!VcTicker.shouldAnimate(this.motion.matches, copies)) {
        this.setAttribute('data-static', '');
        this.lastListWidth = this.list.scrollWidth;
        return;
      }

      this.setAttribute('data-animated', '');
      for (let i = 1; i < copies; i += 1) {
        const clone = this.list.cloneNode(true);
        clone.setAttribute('data-ticker-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        clone.removeAttribute('aria-label');
        clone.querySelectorAll('a').forEach((link) => { link.tabIndex = -1; });
        this.track.appendChild(clone);
      }
      this.style.setProperty('--vc-ticker-shift', `-${listWidth}px`);
      this.style.setProperty('--vc-ticker-duration', `${VcTicker.durationSeconds(listWidth, this.speed)}s`);
      this.lastListWidth = this.list.scrollWidth;
      this.setPaused(this.userPaused);
    }

    setPaused(paused) {
      this.toggleAttribute('data-paused', paused);
      if (!this.pauseButton) return;
      this.pauseButton.setAttribute('aria-pressed', String(paused));
      this.pauseButton.setAttribute('aria-label', paused ? this.dataset.labelPlay : this.dataset.labelPause);
    }
  }

  customElements.define('vc-ticker-bar', VcTickerBar);
})(globalThis);
